import { Divider, Box as MuiBox, Popper, Stack, styled, SxProps, TextField, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useSession } from "../../hooks";
import { useGetPlanActionItems, useGetTeams, type GetPlanActionItemDto } from "../../http";
import { ITeamSummary } from "../../models";
import { AutocompleteOption } from "..";
import { Autocomplete } from "../Autocomplete";
import { isWhitespace } from "../textEditor";

const Box = styled(MuiBox)`
  &:first-child {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }

  &:last-child {
    margin-bottom: 2px;
  }
` as typeof MuiBox;

interface UrlParams {
  slug: string;
}

interface IProps {
  actionItemGuid?: string;
  disabled?: boolean;
  showLabels?: boolean;
  teamOptionDisabled?: (team: ITeamSummary) => boolean;
  defaultContext?: {
    teamSlug: string;
    actionItemGuid: string;
  };
  shouldHideChildren?: boolean;
  shouldHideParents?: boolean;
  onSelect: (actionItem: GetPlanActionItemDto | null) => void;
  sx?: SxProps;
}

const StrategicContextPicker = (props: IProps) => {
  const { slug } = useParams<keyof UrlParams>() as UrlParams;
  const { userId } = useSession();
  const [selectedTeam, setSelectedTeam] = useState<ITeamSummary | null>(null);
  const [selectedActionItem, setSelectedActionItem] = useState<GetPlanActionItemDto | null>(null);
  const { data: teams, isLoading: isLoadingTeams } = useGetTeams(userId);
  const { data: planActionItems, isFetching: isFetchingPlanActionItems } = useGetPlanActionItems(selectedTeam?.slug);
  const theme = useTheme();

  useEffect(() => {
    if (props.defaultContext) {
      setSelectedTeam(teams?.find((x) => x.slug === props.defaultContext?.teamSlug && x.planGuid !== null) ?? null);
    } else {
      setSelectedTeam(teams?.find((x) => x.slug === slug && x.planGuid !== null) ?? null);
    }
  }, [props.defaultContext, slug, teams]);

  useEffect(() => {
    if (props.defaultContext) {
      const actionItem =
        planActionItems?.actionItems.find((x) => x.actionItemGuid === props.defaultContext?.actionItemGuid) ?? null;
      setSelectedActionItem(actionItem);
      props.onSelect(actionItem);
    } else {
      setSelectedActionItem(null);
      props.onSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultContext, planActionItems]);

  function handleTeamChanged(team: ITeamSummary | null) {
    setSelectedTeam(team);
    setSelectedActionItem(null);
  }

  function handleConnectionPointChanged(actionItem: GetPlanActionItemDto | null) {
    setSelectedActionItem(actionItem);
    props.onSelect(actionItem);
  }

  function sortFunction(a: ITeamSummary, b: ITeamSummary) {
    const aMembership = a.isAdministrator || a.isMember;
    const bMembership = b.isAdministrator || b.isMember;

    if (aMembership !== bMembership) {
      return aMembership ? -1 : 1;
    }

    return a.name.localeCompare(b.name);
  }

  return (
    <Stack spacing={props.showLabels ? 1 : 0.5} sx={props.sx}>
      <Stack spacing={0.25}>
        {props.showLabels && <Typography variant="label">Team</Typography>}
        <Autocomplete
          isLoading={isLoadingTeams}
          disabled={isLoadingTeams || props.disabled}
          groupBy={(option) => (option.isMember || option.isAdministrator ? "My Teams" : "Other Teams")}
          options={teams ? [...teams].sort(sortFunction) : []}
          getOptionLabel={(option) => option.name}
          renderGroup={(group) => (
            <Box key={group.key} sx={{ pb: 0.25 }}>
              <Stack
                spacing={0.2}
                sx={{ px: 0.5, pt: 0.75, position: "sticky", top: 0, backgroundColor: theme.palette.common.white }}
              >
                <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
                <Divider />
              </Stack>
              {group.children}
            </Box>
          )}
          renderOption={(props, option) => (
            <AutocompleteOption {...props} key={option.slug}>
              <Stack>
                <Typography>{option.name}</Typography>
                {option.planGuid === null && <Typography variant="caption">This team has no strategic map</Typography>}
              </Stack>
            </AutocompleteOption>
          )}
          getOptionDisabled={(option) => option.planGuid === null || props.teamOptionDisabled?.(option) === true}
          isOptionEqualToValue={(option, value) => option.slug === value.slug}
          renderInput={(params) => <TextField {...params} placeholder="Select a team..." />}
          onChange={(_, value) => handleTeamChanged(value)}
          slots={{
            popper: (props) => (
              <Popper {...props} style={{ ...props.style, margin: 0, padding: 0, minWidth: "300px" }} />
            ),
          }}
          value={selectedTeam}
        />
      </Stack>
      <Stack spacing={0.25}>
        {props.showLabels && <Typography variant="label">Strategic Item</Typography>}
        <Autocomplete
          isLoading={isFetchingPlanActionItems}
          disabled={!selectedTeam || !planActionItems || props.disabled}
          groupBy={(option) => option.sectionName}
          options={
            planActionItems?.actionItems.filter(
              (x) =>
                x.actionItemGuid !== props.actionItemGuid &&
                x.description &&
                !isWhitespace(x.description) &&
                !(
                  props.shouldHideChildren &&
                  props.actionItemGuid &&
                  x.recursiveParentContextActionItemGuids.includes(props.actionItemGuid)
                ) &&
                !(
                  props.shouldHideParents &&
                  props.actionItemGuid &&
                  x.recursiveChildContextActionItemGuids.includes(props.actionItemGuid)
                ),
            ) ?? []
          }
          getOptionLabel={(option) => option.description!}
          renderGroup={(group) => (
            <Box key={group.key} sx={{ pb: 0.25 }}>
              <Stack
                spacing={0.2}
                sx={{ px: 0.5, pt: 0.75, position: "sticky", top: 0, backgroundColor: theme.palette.common.white }}
              >
                <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
                <Divider />
              </Stack>
              {group.children}
            </Box>
          )}
          renderOption={(props, option) => (
            <AutocompleteOption {...props} key={option.actionItemGuid}>
              <Typography>{option.description}</Typography>
            </AutocompleteOption>
          )}
          isOptionEqualToValue={(option, value) => option.actionItemGuid === value.actionItemGuid}
          renderInput={(params) => <TextField {...params} placeholder="Select a strategic map item..." />}
          onChange={(_, value) => handleConnectionPointChanged(value)}
          slots={{
            popper: (props) => (
              <Popper {...props} style={{ ...props.style, margin: 0, padding: 0, minWidth: "300px" }} />
            ),
          }}
          value={selectedActionItem}
        />
      </Stack>
    </Stack>
  );
};

export { StrategicContextPicker };
